import { Grid2, Typography } from "@mui/material";
import { useEffect, useMemo } from "react";
import StatusBox from "../../../components/StatusBox";
import { useTranslate } from "../../../hooks/useTranslate";
import SummaryBox from "../../../components/SummaryBox";
import StudentDetailsForm from "./components/StudentDetailsForm";
import { useStudents } from "../../../context/students";
import { useGetSingleStudent } from "../../../hooks/useGetSingleStudent";
import Preloader from "../../../components/Preloader";
import useStatuses from "../../../context/status/useStatuses";
import VerificationStatusChip from "../../../components/VerificationStatusChip";
import { KEY_APPROVED, KEY_NULL, KEY_PROGRESS } from "../../../utils/getVerificationStatusKey";

const StudentDetails = () => {
    const { t } = useTranslate();
    const { activeStudentId, activeStudent, refetchStudents } = useStudents();
    const { student, isLoading: isLoadingStudent, refetch: refetchStudent } = useGetSingleStudent(activeStudentId);
    const {
        statuses, isLockedStatus, isRequiredDBS, isRequiredOutOfYear, stepsProgress,
        refetchVerificationStatus, refetchGeneralStatuses,
    } = useStatuses();
    const isLocked = isLockedStatus.responsibleAdults;

    const studentData = useMemo(() => ({ ...student, ...activeStudent }), [activeStudent, student]);

    useEffect(() => {
        refetchVerificationStatus();
    }, []);

    const studentStatus = useMemo(() => {
        if (stepsProgress.studentDetails.completed) {
            return { key: KEY_APPROVED, statusTextKey: "status.complete", isSuccess: true };
        }
        if (statuses.studentVerification.key === KEY_PROGRESS) {
            return { key: KEY_PROGRESS, statusTextKey: "status.verifying", isInfo: true };
        }
        return { key: KEY_NULL, statusTextKey: "status.incomplete", isWarning: true };
    }, [stepsProgress.studentDetails, statuses.studentVerification]);

    const onDetailsSave = () => {
        refetchStudents();
        refetchStudent();
        refetchVerificationStatus();
        refetchGeneralStatuses();
    };

    return (
        <div className="page-content">
            <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, md: 7, lg: 8 }}>
                    <Typography variant="h1">
                        {t("studentDetails.title")}
                    </Typography>
                    <Typography variant="h3" className="mt-4" component="div">
                        {t("studentDetails.subtitle")}
                    </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 5, lg: 4 }}>
                    <StatusBox
                        title={t("studentDetails.studentDetailsStatus")}
                        isSuccess={studentStatus.isSuccess}
                        isWarning={studentStatus.isWarning}
                        isInfo={studentStatus.isInfo}
                    >
                        <Typography variant="body2">
                            {t("studentDetails.studentDetailsStatusDescription")}
                        </Typography>
                        <VerificationStatusChip status={studentStatus} />
                    </StatusBox>
                </Grid2>
            </Grid2>
            <SummaryBox
                className="mt-12 mb-12"
                isFormBox
            >
                <Preloader isLoading={isLoadingStudent}>
                    <StudentDetailsForm
                        isRequiredDBS={isRequiredDBS}
                        isRequiredOutOfYear={isRequiredOutOfYear}
                        isDisabled={isLocked}
                        data={studentData}
                        onSuccess={onDetailsSave}
                    />
                </Preloader>
            </SummaryBox>
        </div>
    );
};

export default StudentDetails;
